import { useContext } from 'react';

import { addMonths, addYears, format, getYear } from 'date-fns';
import styled from 'styled-components';

import { Button } from '@components/button';
import { IconButton } from '@components/icon-button';
import { IconChevronRightSmall } from '@components/icons/chevron-right-small';
import { VisuallyHidden } from '@components/visually-hidden';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { dateFnsLocales } from '@utils/date';

import { ArrowNavContext } from './arrow-nav';
import { getYearPeriod, YEAR_GRID_SIZE } from './helpers';
import { CalendarView, LanguageOptions } from './types';

const headerLabels = {
  en: { previous: 'Previous', next: 'Next' },
  is: { previous: 'Fyrri', next: 'Næsta' },
};

type CalendarHeaderProps = {
  view: CalendarView;
  date: Date;
  lang: LanguageOptions;
  onViewChange: (view: CalendarView) => void;
  onViewDateChange: (date: Date) => void;
};

export const CalendarHeader = (props: CalendarHeaderProps) => {
  const { view, date, lang, onViewChange, onViewDateChange } = props;
  const arrowNav = useContext(ArrowNavContext);
  const labels = headerLabels[lang];

  const headerLabel = (() => {
    if (view === 'day') return format(date, 'MMMM yyyy', { locale: dateFnsLocales[lang] });
    if (view === 'month') return String(getYear(date));
    const [startPeriod, endPeriod] = getYearPeriod(getYear(date));
    return `${startPeriod} - ${endPeriod}`;
  })();

  const stepBy = (direction: -1 | 1) => {
    if (view === 'day') return onViewDateChange(addMonths(date, direction));
    if (view === 'month') return onViewDateChange(addYears(date, direction));
    onViewDateChange(addYears(date, direction * YEAR_GRID_SIZE));
  };

  const drillDown = () => {
    if (view === 'day') onViewChange('month');
    else if (view === 'month') onViewChange('year');
  };

  const isHeaderActionable = view !== 'year';

  // In the popover, the header controls are out of the page tab order; Tab is
  // trapped and cycled within the popover instead (handlePopupKeyDown in
  // datepicker.tsx). Inline keeps them normally tabbable.
  const tabIndex = arrowNav ? -1 : undefined;
  return (
    <HeaderRow>
      <IconButton
        variant="secondary-purple"
        aria-label={labels.previous}
        dataTestid="datepicker-prev"
        icon={<ChevronLeft />}
        tabIndex={tabIndex}
        onClick={() => stepBy(-1)}
      />
      {isHeaderActionable ? (
        <Button
          variant="secondary"
          size="md"
          isFullWidth
          tabIndex={tabIndex}
          dataTestid="datepicker-header"
          onClick={drillDown}
        >
          {headerLabel}
        </Button>
      ) : (
        <HeaderLabel data-testid="datepicker-header">{headerLabel}</HeaderLabel>
      )}
      <IconButton
        variant="secondary-purple"
        aria-label={labels.next}
        dataTestid="datepicker-next"
        icon={<IconChevronRightSmall />}
        tabIndex={tabIndex}
        onClick={() => stepBy(1)}
      />
      {/* Announce month/period changes to screen readers (the APG dialog
          example wires its heading aria-live for the same reason). Arrow-key
          navigation already self-announces — the newly focused day button's
          label carries the full date — but clicking prev/next moves no focus,
          so without this the view changes silently. */}
      <VisuallyHidden role="status">{headerLabel}</VisuallyHidden>
    </HeaderRow>
  );
};

const HeaderRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: pxToRem(8),
  width: '100%',
});

const ChevronLeft = styled(IconChevronRightSmall)({
  transform: 'scaleX(-1)',
});

// The year-view header (e.g. "2017 - 2028") isn't actionable, so it's a plain
// label rather than a Button. It mirrors the secondary Button's box — full width
// between the arrows, theme.base.height tall, semibold14/textSecondary — so the
// header keeps the same footprint across day/month/year views, minus the fill
// since it isn't clickable.
const HeaderLabel = styled.span({
  display: 'flex',
  flex: 1,
  minWidth: 0,
  alignItems: 'center',
  justifyContent: 'center',
  height: theme.base.height,
  font: theme.font.semibold14,
  color: theme.color.textSecondary,
  whiteSpace: 'nowrap',
});
